import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { mqMin } from 'utils/responsive';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 40px;
`;

const Title = styled.h1`
    font-weight: 700;
    font-size: 18px;
`;

const QAContainer = styled.section`
    width: 88.37209302325581%;
    border-top: solid 2px var(--dark-blue);
    border-bottom: solid 2px var(--dark-blue);
    margin: 10px;

    ${mqMin('NARROW')} {
        padding: 10px 0 10px 0;
    }
`;

const Item = styled.section`
    width: 100%;
    border-bottom: solid 1px #dadada;

    p {
        font-size: var(--font-medium);
        font-weight: var(--font-bold);
    }
`;

const QButton = styled.div`
    width: 100%;
    position: relative;
    background-color: #fff;
    color: var(--dark-blue);
    padding: 0;
    cursor: pointer;
`;

const QTextContainer = styled.div`
    display: flex;
    align-items: center;

    p {
        width: calc(100% - 24px);
        padding: 12px 20px;
        text-align: left;
        line-height: 1.5em;
        letter-spacing: 0.1em;
    }
`;

const QMark = styled.div<{ isOpen: boolean }>`
    position: relative;
    display: block;
    width: 20px;
    height: 20px;
    margin-right: 15px;
    span:nth-of-type(1) {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        display: block;
        width: 100%;
        height: 2px;
        background-color: var(--dark-blue);
    }
    span:nth-of-type(2) {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        display: block;
        width: 2px;
        height: 100%;
        background-color: var(--dark-blue);
        transition: transform 0.2s;
        transform: ${({ isOpen }) => (isOpen ? 'rotate(90deg)' : 'rotate(0deg)')};
    }
`;

const AItem = styled.div<{ isOpen: boolean }>`
    width: 100%;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    background-color: var(--white);
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
`;

const AItemText = styled.div`
    padding: 0 20px 20px 20px;
    text-align: left;
    line-height: 1.7em;
    letter-spacing: 0.1em;
    font-weight: 600;

    p:nth-of-type(2) {
        color: var(--dark-blue);
    }
`;

const OrangeText = styled.p`
    color: var(--orange);
`;

const faqData = [
    {
        question: 'どのような会社を買えば、うまくいくのでしょうか？',
        answer: (
            <>
                <OrangeText>「安い会社」より「自社とのシナジーがある会社」を選ぶことが成功の鍵です。</OrangeText>
                <p>
                    業種の近さ、顧客基盤、人材、技術などの面で自社との補完関係があるかを見極めましょう。
                    <br />
                    戦略との整合性が明確な案件ほど、PMI（統合）もうまく進みます。
                </p>
            </>
        ),
    },
    {
        question: 'どこまで情報を開示してもらえるのですか？',
        answer: (
            <>
                <OrangeText>初期段階では、匿名資料（ノンネーム）での概要提示が主流です。</OrangeText>
                <p>
                    NDA（秘密保持契約）を締結した後、社名・財務情報・組織構成・事業詳細などのフルパッケージ資料の提供へ進みます。
                    <br />
                    開示範囲については、都度売り手との調整を行うケースが多いです。
                </p>
            </>
        ),
    },
    {
        question: '仲介会社によって、提案内容は違うのですか？',
        answer: (
            <>
                <OrangeText>売り案件は専任契約が多いので、仲介会社によって提案される案件が異なります。</OrangeText>
                <p>自社が希望する案件を持っている仲介会社を見つけることが、M&Aの実行に向けての第一歩です。</p>
            </>
        ),
    },
    {
        question: 'M&Aを進めるベストなタイミングはありますか？',
        answer: (
            <>
                <OrangeText>
                    「資金があるとき」「人材が足りないとき」「成長の壁を感じたとき」などが一つの目安です。
                </OrangeText>
                <p>
                    また、市場環境や事業フェーズによって「今買うべきか」の判断は変わります。
                    <br />
                    まずは1社と出会ってみることで、自社にとっての最適なタイミングが見えてきます。
                </p>
            </>
        ),
    },
    {
        question: '交渉や契約はどうやって進めればいいですか？',
        answer: (
            <>
                <OrangeText>
                    専門の仲介会社やアドバイザーが間に入り、条件交渉・書類作成・スケジュール管理まで支援します。
                </OrangeText>
                <p>自社で全て行う必要はなく、M&A初心者でもプロと連携しながら進めることが可能です。</p>
            </>
        ),
    },
];

export const QA: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

    const toggleFAQ = (index: number) => {
        setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
    };

    useEffect(() => {
        answerRefs.current.forEach((ref, index) => {
            if (ref) {
                if (openIndex === index) {
                    ref.style.maxHeight = ref.scrollHeight + 'px';
                } else {
                    ref.style.maxHeight = '0';
                }
            }
        });
    }, [openIndex]);

    return (
        <Wrapper>
            <Title>よくあるご質問</Title>
            <QAContainer>
                {faqData.map((faq, index) => (
                    <Item key={index}>
                        <QButton className="topFaq_item_q" onClick={() => toggleFAQ(index)}>
                            <QTextContainer className="topFaq_item_q_text">
                                <p>{faq.question}</p>
                                <QMark isOpen={openIndex === index}>
                                    <span className="topFaq_item_q_mark_line01"></span>
                                    <span className="topFaq_item_q_mark_line02"></span>
                                </QMark>
                            </QTextContainer>
                        </QButton>
                        <AItem ref={(el) => (answerRefs.current[index] = el)} isOpen={openIndex === index}>
                            <AItemText>{faq.answer}</AItemText>
                        </AItem>
                    </Item>
                ))}
            </QAContainer>
        </Wrapper>
    );
};
